import { useForm } from "react-hook-form";
import { Badge, Button, Flex, Text } from "@mantine/core";
import resolver from "@congrs/resolver";
import { fullFormats } from "ajv-formats/dist/formats";
import {
  useAddLanguage,
  useGetAllSelectableLanguages,
  useUpdateLanguage,
} from "../../api/languages";
import { TextInput, Select, Switch } from "react-hook-form-mantine";
import { web_addLanguageSchema } from "@congrs/schemas";
import { modals } from "@mantine/modals";

const LanguageModal = ({ action = "add", languageData = null }) => {
  const { mutate: addLanguage, isPending: isAdding } = useAddLanguage();
  const { mutate: updateLanguage, isPending: isUpdating } = useUpdateLanguage();
  const { data: selectableLanguages } = useGetAllSelectableLanguages();

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    setValue,
    reset,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      code: languageData?.code || "",
      name: languageData?.name || "",
      nativeName: languageData?.nativeName || "",
      isActive: languageData?.isActive || "",
      isDefault: languageData?.isDefault || "",
    },
    resolver: resolver(web_addLanguageSchema, { formats: fullFormats }),
  });

  const onSubmit = (data) => {
    if (action === "add") {
      addLanguage(data, {
        onSuccess: () => {
          reset();
          modals.close("languageModal");
        },
      });
    } else if (action === "edit") {
      updateLanguage(data, {
        onSuccess: () => {
          reset();
          modals.close("languageModal");
        },
      });
    }
  };

  const isActive = watch("isActive");
  const isDefault = watch("isDefault");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {action === "add" ? (
        <>
          <Select
            label="Select language"
            name="code"
            placeholder="English, Russian, Serbian..."
            searchable
            clearable
            control={control}
            data={
              selectableLanguages?.map((l) => {
                return { value: l.code, label: l.name };
              }) || []
            }
            onChange={(value) => {
              const selectedLang = selectableLanguages?.find(
                (lang) => lang.code === value
              );
              if (selectedLang) {
                setValue("name", selectedLang.name, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue("nativeName", selectedLang.nativeName, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              } else {
                setValue("name", "", {
                  shouldDirty: true,
                  shouldValidate: false,
                });
                setValue("nativeName", "", {
                  shouldDirty: true,
                  shouldValidate: false,
                });
              }
            }}
          />

          <Button
            fullWidth
            mt="md"
            size="sm"
            type="submit"
            loading={isAdding}
            color="blue"
            h="40px"
            disabled={!isDirty || !isValid}
          >
            Add Language
          </Button>
        </>
      ) : (
        <>
          <TextInput
            label="Language Name"
            type="text"
            name="name"
            control={control}
            withAsterisk
            placeholder="Language name"
          />
          <TextInput
            label="Native Name"
            type="text"
            name="nativeName"
            control={control}
            mt="md"
            withAsterisk
            placeholder="Language native name"
          />
          <Flex direction="column" gap={20} align="flex-start" mt="md">
            <Flex align="center" gap={5} direction="row">
              <Switch
                size="md"
                disabled={languageData?.isDefault}
                color="green"
                name="isActive"
                control={control}
              />
              <Badge color={isActive ? "green" : "gray"} variant="light">
                active
              </Badge>
              <Text size="sm" c="dimmed" fs="italic">
                Makes language active to use in events
              </Text>
            </Flex>

            <Flex align="center" gap={5} direction="row">
              <Switch
                size="md"
                color="yellow"
                name="isDefault"
                control={control}
                disabled={languageData?.isDefault}
              />
              <Badge color={isDefault ? "yellow" : "gray"} variant="light">
                default
              </Badge>
              <Text size="sm" c="dimmed" fs="italic">
                Sets default app language
              </Text>
            </Flex>
          </Flex>

          <Button
            fullWidth
            mt="md"
            size="sm"
            type="submit"
            loading={isUpdating}
            color="blue"
            h="40px"
            disabled={!isDirty || !isValid}
          >
            Submit changes
          </Button>
        </>
      )}
    </form>
  );
};

export const openLanguageModal = ({ action = "add", languageData = null }) => {
  modals.open({
    modalId: "languageModal",
    title: action === "edit" ? "Edit Language" : "Add Language",
    children: <LanguageModal action={action} languageData={languageData} />,
  });
};

export default LanguageModal;
